import { EntityType } from 'components/blocks/selector/types'
import { isInDummyMode } from 'dummy/static'

/**
 * Storage keys for entity selections.
 * Note: limited mapping for backward compatibility.
 */
const STORAGE_KEYS: Partial<Record<EntityType, string>> = {
  contract: 'selectedContractId'
} as const

/**
 * URL parameter names for entity selections.
 * Note: limited mapping for backward compatibility.
 */
const URL_PARAM_MAP: Partial<Record<EntityType, string>> = {
  contract: 'contractId'
} as const

type EntitySchema = EntityType

/**
 * Build URL path with entity context preserved.
 * Prefers localStorage for latest selections and can filter by allowed schemas.
 * @param currentSearch - Optional current search string from React Router location for reactivity
 */
export const buildPathWithEntityContext = (
  pathname: string,
  additionalParams?: Record<string, string>,
  allowedSchemas?: string[],
  currentSearch?: string | undefined
): { pathname: string; search: string } => {
  const currentParams = new URLSearchParams(
    currentSearch ?? window.location.search
  )
  const allSelectedIds = getAllSelectedEntityIds(
    currentParams,
    true,
    allowedSchemas
  )

  // Merge current entity IDs with any additional params
  const mergedParams = { ...allSelectedIds, ...additionalParams }

  // Ensure deterministic key order for query string (alphabetical by key)
  const searchParams = new URLSearchParams()

  Object.keys(mergedParams)
    .sort()
    .forEach((key) => {
      const value = mergedParams[key]

      if (value) {
        searchParams.set(key, value)
      }
    })

  return {
    pathname,
    search: searchParams.toString()
  }
}

/**
 * Build URL string with entity context preserved.
 * Can optionally filter to only include schemas used by the target page.
 * @param currentSearch - Optional current search string from React Router location for reactivity
 */
export const buildUrlWithEntityContext = (
  pathname: string,
  additionalParams?: Record<string, string>,
  allowedSchemas?: string[],
  currentSearch?: string
): string => {
  const { pathname: path, search } = buildPathWithEntityContext(
    pathname,
    additionalParams,
    allowedSchemas,
    currentSearch
  )

  return search ? `${path}?${search}` : path
}

/**
 * Get all selected entity IDs from URL and localStorage.
 * When building navigation URLs, prefers localStorage to avoid race conditions.
 * Can optionally filter to only include specific schemas.
 * If allowedSchemas is undefined, includes all schemas (no filtering).
 * If allowedSchemas is an empty array, includes no schemas (filters all).
 */
export const getAllSelectedEntityIds = (
  searchParams?: URLSearchParams,
  preferLocalStorage = true,
  allowedSchemas?: string[]
): Record<string, string> => {
  const params = searchParams || new URLSearchParams(window.location.search)
  const result: Record<string, string> = {}

  Object.entries(URL_PARAM_MAP).forEach(([schema, urlParam]) => {
    if (allowedSchemas !== undefined && !allowedSchemas.includes(schema)) {
      return
    }

    if (!urlParam) {
      return
    }

    const id = getSelectedEntityId(
      schema as EntitySchema,
      params,
      preferLocalStorage
    )

    if (id) {
      result[urlParam] = id
    }
  })

  return result
}

/**
 * Get selected entity ID from URL or localStorage.
 * Prefers localStorage when building navigation URLs; prefers URL for current state.
 */
export const getSelectedEntityId = (
  schema: EntitySchema,
  searchParams?: URLSearchParams,
  preferLocalStorage = false
): string | null => {
  if (isInDummyMode()) {
    return null
  }

  const storageKey = STORAGE_KEYS[schema]
  const urlParam = URL_PARAM_MAP[schema]
  const params = searchParams || new URLSearchParams(window.location.search)

  const urlValue = urlParam ? params.get(urlParam) : null
  const storageValue = storageKey ? localStorage.getItem(storageKey) : null

  if (preferLocalStorage) {
    return storageValue || urlValue
  }

  return urlValue || storageValue
}

/**
 * Get URL parameter name for an entity type.
 */
export const getUrlParamForEntityType = (
  schema: EntityType
): string | undefined => {
  return URL_PARAM_MAP[schema]
}

/**
 * Restore entity selections from localStorage on app init.
 * Returns params to be merged into the URL if any were restored.
 */
export const restoreEntitySelectionsFromStorage = (): URLSearchParams => {
  if (isInDummyMode()) {
    return new URLSearchParams()
  }

  const currentParams = new URLSearchParams(window.location.search)
  let hasChanges = false

  Object.entries(URL_PARAM_MAP).forEach(([schema, urlParam]) => {
    if (!urlParam) {
      return
    }

    if (!currentParams.has(urlParam)) {
      const storedId = getSelectedEntityId(
        schema as EntitySchema,
        currentParams,
        true
      )

      if (storedId) {
        currentParams.set(urlParam, storedId)
        hasChanges = true
      }
    }
  })

  return hasChanges ? currentParams : new URLSearchParams()
}

/**
 * Save selected entity ID to localStorage.
 */
export const saveSelectedEntityId = (
  schema: EntitySchema,
  entityId: string | null
): void => {
  if (isInDummyMode()) {
    return
  }

  const storageKey = STORAGE_KEYS[schema]

  if (!storageKey) {
    return
  }

  if (entityId) {
    localStorage.setItem(storageKey, entityId)
  } else {
    localStorage.removeItem(storageKey)
  }
}
